import { byId, isAr, message, copyText, trackEvent } from './extra-common.js';

// RFC 3492 Punycode Implementation (Zero dependencies, pure client-side)
const PUNYCODE = (() => {
  const maxInt = 2147483647;
  const base = 36;
  const tMin = 1;
  const tMax = 26;
  const skew = 38;
  const damp = 700;
  const initialBias = 72;
  const initialN = 128;
  const delimiter = '-';

  function adapt(delta, numPoints, firstTime) {
    let k = 0;
    delta = firstTime ? Math.floor(delta / damp) : delta >> 1;
    delta += Math.floor(delta / numPoints);
    for (; delta > ((base - tMin) * tMax) >> 1; k += base) {
      delta = Math.floor(delta / (base - tMin));
    }
    return Math.floor(k + ((base - tMin + 1) * delta) / (delta + skew));
  }

  function decode(input) {
    const output = [];
    const inputLen = input.length;
    let i = 0;
    let n = initialN;
    let bias = initialBias;

    let basic = input.lastIndexOf(delimiter);
    if (basic < 0) basic = 0;

    for (let j = 0; j < basic; ++j) {
      if (input.charCodeAt(j) >= 0x80) throw new RangeError('Illegal input >= 0x80');
      output.push(input.charCodeAt(j));
    }

    let index = basic > 0 ? basic + 1 : 0;
    while (index < inputLen) {
      const oldi = i;
      let w = 1;
      for (let k = base; ; k += base) {
        if (index >= inputLen) throw new RangeError('Invalid input');
        const digit = input.charCodeAt(index++);
        let digitVal;
        if (digit - 48 < 10) digitVal = digit - 22;
        else if (digit - 65 < 26) digitVal = digit - 65;
        else if (digit - 97 < 26) digitVal = digit - 97;
        else throw new RangeError('Invalid digit');

        i += digitVal * w;
        const t = k <= bias ? tMin : (k >= bias + tMax ? tMax : k - bias);
        if (digitVal < t) break;
        w *= (base - t);
      }
      const outLen = output.length + 1;
      bias = adapt(i - oldi, outLen, oldi === 0);
      n += Math.floor(i / outLen);
      i %= outLen;
      output.splice(i++, 0, n);
    }
    return String.fromCodePoint(...output);
  }

  function encode(input) {
    const output = [];
    const codePoints = [];
    for (const ch of input) {
      codePoints.push(ch.codePointAt(0));
    }
    const inputLen = codePoints.length;
    let n = initialN;
    let delta = 0;
    let bias = initialBias;

    for (const cp of codePoints) {
      if (cp < 0x80) output.push(String.fromCharCode(cp));
    }
    const basicCount = output.length;
    let handled = basicCount;

    if (basicCount > 0) output.push(delimiter);

    while (handled < inputLen) {
      let m = maxInt;
      for (const cp of codePoints) {
        if (cp >= n && cp < m) m = cp;
      }
      delta += (m - n) * (handled + 1);
      n = m;

      for (const cp of codePoints) {
        if (cp < n) {
          if (++delta > maxInt) throw new RangeError('Overflow');
        }
        if (cp === n) {
          let q = delta;
          for (let k = base; ; k += base) {
            const t = k <= bias ? tMin : (k >= bias + tMax ? tMax : k - bias);
            if (q < t) break;
            const qMinusT = q - t;
            const baseMinusT = base - t;
            const digit = t + (qMinusT % baseMinusT);
            output.push(String.fromCharCode(digit + (digit < 26 ? 97 : 22)));
            q = Math.floor(qMinusT / baseMinusT);
          }
          output.push(String.fromCharCode(q + (q < 26 ? 97 : 22)));
          bias = adapt(delta, handled + 1, handled === basicCount);
          delta = 0;
          ++handled;
        }
      }
      ++delta;
      ++n;
    }
    return output.join('');
  }

  function toASCII(domain) {
    return domain.split('.').map(part => {
      return /[^\x00-\x7F]/.test(part) ? 'xn--' + encode(part) : part;
    }).join('.');
  }

  function toUnicode(domain) {
    return domain.split('.').map(part => {
      return part.toLowerCase().startsWith('xn--') ? decode(part.slice(4)) : part;
    }).join('.');
  }

  return { encode, decode, toASCII, toUnicode };
})();

const input = byId('ue-input');
const output = byId('ue-output');
const copyBtn = byId('ue-copy');
const inspector = byId('ue-inspector');

function run(fn) {
  trackEvent('tool_started', 'url-encoder');
  try {
    output.value = fn(input.value);
    message('ue-status', isAr ? 'تم التحويل بنجاح.' : 'Conversion complete.', 'success');
    if (copyBtn) copyBtn.disabled = false;
    trackEvent('tool_completed', 'url-encoder');
  } catch {
    output.value = '';
    message('ue-status', isAr ? 'النص لا يحتوي ترميز URL صالحًا.' : 'The input contains invalid URL encoding.', 'error');
    if (copyBtn) copyBtn.disabled = true;
    trackEvent('tool_failed', 'url-encoder');
  }
}

// Convert domain to ASCII Punycode
function runPunycodeToAscii() {
  trackEvent('tool_started', 'url-encoder');
  try {
    const val = (input?.value || '').trim();
    if (!val) {
      message('ue-status', isAr ? 'يرجى إدخال نطاق أو رابط.' : 'Please enter a domain or URL.', 'error');
      return;
    }
    let res = '';
    if (val.includes('://')) {
      const u = new URL(val);
      u.hostname = PUNYCODE.toASCII(u.hostname);
      res = u.toString();
    } else {
      res = PUNYCODE.toASCII(val);
    }
    output.value = res;
    message('ue-status', isAr ? 'تم تحويل النطاق إلى Punycode (xn--).' : 'Converted domain to Punycode.', 'success');
    if (copyBtn) copyBtn.disabled = false;
    trackEvent('tool_completed', 'url-encoder');
  } catch {
    message('ue-status', isAr ? 'تعذر تحويل النطاق إلى Punycode.' : 'Failed to convert to Punycode.', 'error');
    trackEvent('tool_failed', 'url-encoder');
  }
}

// Convert domain from Punycode to Unicode
function runPunycodeToUnicode() {
  trackEvent('tool_started', 'url-encoder');
  try {
    const val = (input?.value || '').trim();
    if (!val) {
      message('ue-status', isAr ? 'يرجى إدخال نطاق أو رابط Punycode.' : 'Please enter a Punycode domain or URL.', 'error');
      return;
    }
    let res = '';
    if (val.includes('://')) {
      const u = new URL(val);
      u.hostname = PUNYCODE.toUnicode(u.hostname);
      res = u.toString();
    } else {
      res = PUNYCODE.toUnicode(val);
    }
    output.value = res;
    message('ue-status', isAr ? 'تم تحويل Punycode إلى الحروف الأصلية.' : 'Converted Punycode to Unicode.', 'success');
    if (copyBtn) copyBtn.disabled = false;
    trackEvent('tool_completed', 'url-encoder');
  } catch {
    message('ue-status', isAr ? 'تعذر فك ترميز Punycode.' : 'Failed to decode Punycode.', 'error');
    trackEvent('tool_failed', 'url-encoder');
  }
}

// URL Breakdown and Parameters Inspector
let parsedUrlObj = null;

function parseCurrentUrl() {
  const raw = (input?.value || output?.value || '').trim();
  if (!raw) {
    message('ue-status', isAr ? 'يرجى إدخال رابط صالح لتفكيكه.' : 'Please enter a valid URL to inspect.', 'error');
    return;
  }
  try {
    let testUrl = raw;
    if (!/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(testUrl)) {
      testUrl = 'https://' + testUrl;
    }
    parsedUrlObj = new URL(testUrl);
    renderInspector();
    if (inspector) inspector.hidden = false;
    message('ue-status', isAr ? 'تم تحليل الرابط وتفكيك معاملاته بنجاح.' : 'URL parsed and parameters extracted.', 'success');
  } catch {
    message('ue-status', isAr ? 'الرابط المدخل غير صالح للتحليل.' : 'The entered URL is not valid for parsing.', 'error');
  }
}

function escapeAttr(str) {
  return String(str ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function renderInspector() {
  if (!parsedUrlObj) return;

  const protoEl = byId('ue-meta-proto');
  const hostEl = byId('ue-meta-host');
  const pathEl = byId('ue-meta-path');
  const hashEl = byId('ue-meta-hash');

  if (protoEl) protoEl.value = parsedUrlObj.protocol;
  if (hostEl) hostEl.value = parsedUrlObj.host;
  if (pathEl) pathEl.value = parsedUrlObj.pathname;
  if (hashEl) hashEl.value = parsedUrlObj.hash;

  // Query Params List
  const paramsList = byId('ue-params-list');
  if (paramsList) {
    paramsList.innerHTML = '';
    const entries = Array.from(parsedUrlObj.searchParams.entries());
    if (entries.length === 0) {
      const emptyDiv = document.createElement('div');
      emptyDiv.className = 'ue-empty-params';
      emptyDiv.textContent = isAr ? 'لا توجد معاملات استعلام (Query Parameters) حالياً.' : 'No query parameters found.';
      paramsList.appendChild(emptyDiv);
    } else {
      entries.forEach(([key, val], idx) => {
        const row = document.createElement('div');
        row.className = 'ue-param-row';
        row.innerHTML = `
          <input type="text" class="ue-p-key" value="${escapeAttr(key)}" placeholder="${isAr ? 'المفتاح' : 'Key'}">
          <input type="text" class="ue-p-val" value="${escapeAttr(val)}" placeholder="${isAr ? 'القيمة' : 'Value'}">
          <button type="button" class="btn-secondary btn-sm ue-p-del" data-idx="${idx}" title="${isAr ? 'حذف' : 'Delete'}">&times;</button>
        `;
        paramsList.appendChild(row);
      });
    }
  }

  // Populate UTM fields if present
  const utmSource = byId('ue-utm-source');
  const utmMedium = byId('ue-utm-medium');
  const utmCampaign = byId('ue-utm-campaign');
  const utmTerm = byId('ue-utm-term');
  const utmContent = byId('ue-utm-content');

  if (utmSource) utmSource.value = parsedUrlObj.searchParams.get('utm_source') || '';
  if (utmMedium) utmMedium.value = parsedUrlObj.searchParams.get('utm_medium') || '';
  if (utmCampaign) utmCampaign.value = parsedUrlObj.searchParams.get('utm_campaign') || '';
  if (utmTerm) utmTerm.value = parsedUrlObj.searchParams.get('utm_term') || '';
  if (utmContent) utmContent.value = parsedUrlObj.searchParams.get('utm_content') || '';
}

function applyParamsToUrl() {
  if (!parsedUrlObj) return;

  const protoEl = byId('ue-meta-proto');
  const hostEl = byId('ue-meta-host');
  const pathEl = byId('ue-meta-path');
  const hashEl = byId('ue-meta-hash');

  try {
    if (protoEl && protoEl.value) parsedUrlObj.protocol = protoEl.value.replace(/:?$/, ':');
    if (hostEl && hostEl.value) parsedUrlObj.host = hostEl.value;
    if (pathEl) parsedUrlObj.pathname = pathEl.value;
    if (hashEl) parsedUrlObj.hash = hashEl.value.replace(/^#?/, '#');

    // Reconstruct Search Params
    const newParams = new URLSearchParams();

    // From Query Rows
    const rows = document.querySelectorAll('.ue-param-row');
    rows.forEach(r => {
      const k = r.querySelector('.ue-p-key')?.value.trim();
      const v = r.querySelector('.ue-p-val')?.value;
      if (k) newParams.append(k, v ?? '');
    });

    // From UTM fields (override or set)
    const utmFields = [
      ['utm_source', byId('ue-utm-source')?.value.trim()],
      ['utm_medium', byId('ue-utm-medium')?.value.trim()],
      ['utm_campaign', byId('ue-utm-campaign')?.value.trim()],
      ['utm_term', byId('ue-utm-term')?.value.trim()],
      ['utm_content', byId('ue-utm-content')?.value.trim()]
    ];
    for (const [key, val] of utmFields) {
      if (val) {
        newParams.set(key, val);
      }
    }

    parsedUrlObj.search = newParams.toString();
    const finalUrl = parsedUrlObj.toString();
    output.value = finalUrl;
    if (copyBtn) copyBtn.disabled = false;
    message('ue-status', isAr ? 'تم تحديث الرابط بنجاح مع المعاملات المعدلة.' : 'URL updated with modified parameters.', 'success');
  } catch (err) {
    message('ue-status', isAr ? 'حدث خطأ أثناء تركيب الرابط: ' + err.message : 'Error constructing URL: ' + err.message, 'error');
  }
}

function addEmptyParamRow() {
  const paramsList = byId('ue-params-list');
  if (!paramsList) return;
  const empty = paramsList.querySelector('.ue-empty-params');
  if (empty) empty.remove();

  const row = document.createElement('div');
  row.className = 'ue-param-row';
  row.innerHTML = `
    <input type="text" class="ue-p-key" value="" placeholder="${isAr ? 'المفتاح (Key)' : 'Key'}">
    <input type="text" class="ue-p-val" value="" placeholder="${isAr ? 'القيمة (Value)' : 'Value'}">
    <button type="button" class="btn-secondary btn-sm ue-p-del" title="${isAr ? 'حذف' : 'Delete'}">&times;</button>
  `;
  paramsList.appendChild(row);
}

byId('ue-encode')?.addEventListener('click', () => run(encodeURIComponent));
byId('ue-full')?.addEventListener('click', () => run(encodeURI));
byId('ue-decode')?.addEventListener('click', () => run(decodeURIComponent));
byId('ue-puny-ascii')?.addEventListener('click', runPunycodeToAscii);
byId('ue-puny-uni')?.addEventListener('click', runPunycodeToUnicode);
byId('ue-clear')?.addEventListener('click', () => {
  if (input) input.value = '';
  if (output) output.value = '';
  message('ue-status', '');
  if (copyBtn) copyBtn.disabled = true;
  if (inspector) inspector.hidden = true;
});
byId('ue-copy')?.addEventListener('click', async () => {
  try {
    await copyText(output.value);
    message('ue-status', isAr ? 'تم النسخ.' : 'Copied.', 'success');
    trackEvent('tool_completed', 'url-encoder');
  } catch {
    message('ue-status', isAr ? 'تعذر النسخ.' : 'Copy failed.', 'error');
    trackEvent('tool_failed', 'url-encoder');
  }
});
byId('ue-parse')?.addEventListener('click', parseCurrentUrl);
byId('ue-add-param')?.addEventListener('click', addEmptyParamRow);
byId('ue-apply-params')?.addEventListener('click', applyParamsToUrl);

byId('ue-params-list')?.addEventListener('click', (e) => {
  if (e.target.classList.contains('ue-p-del')) {
    e.target.closest('.ue-param-row')?.remove();
  }
});
